import React, { useState, useEffect, useMemo, useRef, MutableRefObject } from "react";
import {
    TextField,
    PrimaryButton,
    Dropdown,
    IDropdownOption,
    Stack,
    IconButton,
    Callout,
    DirectionalHint,
    TooltipHost,
    Toggle
} from "@fluentui/react";
import { MdOutlineAutoFixHigh } from "react-icons/md";
import { MdOutlineAutoFixOff } from "react-icons/md";
import { useBoolean } from "@fluentui/react-hooks";
import { MdAddCircleOutline } from "react-icons/md";
import { RiDeleteBinLine } from "react-icons/ri";
import { BiMessageSquareEdit } from "react-icons/bi";
import styles from "./DocFieldSelectionV2.module.css";
import {
    dpaAnalysisOptions,
    extrConfConfLabels,
    extrTypeTooltipTexts,
    systemExtrConfigName,
    systemPostConfigName,
    tooltxt_toggle_dpa_extr_auto,
    tooltxt_toggle_dpa_visualai,
    tooltxt_toggle_dpa_highai,
    tooltxt_dropdown_language,
    dpaExtTypeSansSingleTypeCustom,
    defaultContentTypeOptions
} from "../../lsts";

import { DocFieldSelectionV2 } from "../../components/DocFieldSelection/DocFieldSelectionV2";
import {
    tooltipStyles,
    stackStyles,
    dropdownContentTypeStyles,
    textFieldModalContentTypeCreateStyles,
    addUrlCallout,
    toggleStylesAutBaseExtraction,
    dpaFieldConfButStyles,
    textFieldStylesMultilineDocExtrDPA,
    textFieldStylesDocExtrDPA
} from "./../../styles_glob";
import {
    gselectedgroup,
    gloggedinuser,
    gfileassourceoption,
    gcontenttype,
    gextrprop,
    new_content_type_config,
    qaconf,
    qconfemptyprop,
    get_empty_dpa_content_type,
    geninstr,
    emptyGenInstr
} from "../../interfaces";
import { getHeaderG } from "../../api/qpi";

import { cleanValues, cleanTrProps } from "../../util_glob";
import { toast } from "react-toastify";
import { type } from "microsoft-cognitiveservices-speech-sdk/distrib/lib/src/common.speech/SpeechServiceConfig";
import { tooltipHtmlBasic } from "../detailedList/detailedMdls";
import { Tooltip } from "recharts";

interface PropertiesEditorProps {
    selectedGroup: gselectedgroup;
    fileList: gfileassourceoption[];
    loggedInUser: gloggedinuser;
    purpose: string;
    onEditDocExtractionDone: () => void;
}

{/* dpa doc field settings */}

export const DocFieldSettings: React.FC<PropertiesEditorProps> = ({ selectedGroup, fileList, loggedInUser, purpose, onEditDocExtractionDone }) => {
    // space with applied edits
    const [editedSpace, setEditedSpace] = useState<gselectedgroup>({ ...selectedGroup });

    // cont type options for dropdown
    const [contentTypeOptions, setContentTypeOptions] = useState<IDropdownOption[]>(
        editedSpace.contenttype_config.contenttype.map((item: gcontenttype) => ({ key: item.type, text: item.type })) || []
    );

    // cont type options for dropdown
    const [postTypeOptions, setPostTypeOptions] = useState<IDropdownOption[]>(() => {
        const opts = editedSpace.contenttype_config?.postaianalysisoptions?.contenttype?.map((item: gcontenttype) => ({ key: item.type, text: item.type })) || [];
        if (!opts.some(o => o.key === "all_content")) opts.unshift({ key: "all_content", text: "all_content" });
        return opts;
    });

    // selected cont type option for dropdown
    const [contentTypeSelectedOption, setContentTypeSelectedOption] = useState<string>(
        editedSpace.contenttype_config.contenttype.map((item: gcontenttype) => ({ key: item.type, text: item.type }))[0].key || "all_content"
    );

    // selected post cont type option for dropdown
    const [postTypeSelectedOption, setPostTypeSelectedOption] = useState<string>(
        editedSpace.contenttype_config?.postaianalysisoptions?.contenttype?.map((item: gcontenttype) => ({ key: item.type, text: item.type }))[0]?.key || "all_content"
    );

    // selected cont type object
    const [contentTypeDetails, setContentTypeDetails] = useState<gcontenttype>();

    // selected post cont type object
    const [postTypeDetails, setPostTypeDetails] = useState<gcontenttype>();

    // new cont type object
    const [newContentType, setNewContentType] = useState(get_empty_dpa_content_type());

    const [newPostType, setNewPostType] = useState(get_empty_dpa_content_type());

    // api loading state
    const [isLoadingAPI, setIsLoadingAPI] = useState(false);
    const [randomDigits, setRandomDigits] = useState(0);

    // add callout state
    const [isCalloutVisible, { toggle: toggleIsCalloutVisible }] = useBoolean(false);
    const [isPostCalloutVisible, { toggle: toggleIsPostCalloutVisible }] = useBoolean(false);

    // edit callout state
    const [isEditCalloutVisible, { toggle: toggleIsEditCalloutVisible }] = useBoolean(false);
    const [isEditPostCalloutVisible, { toggle: toggleIsEditPostCalloutVisible }] = useBoolean(false);

    // system extraction config name
    const [editContentType, setEditContentType] = useState(get_empty_dpa_content_type());

    // system extraction config name
    const [editPostType, setEditPostType] = useState(get_empty_dpa_content_type());

    // manage selected dropdown option for content type
    useEffect(() => {
        // if no post analysis content types are defined - add default system one
        let contType = editedSpace.contenttype_config?.postaianalysisoptions?.contenttype
        if (contType == undefined || contType?.length == 0) {
            if (dpaExtTypeSansSingleTypeCustom.includes(editedSpace?.contenttype_config?.postaianalysisoptions?.preconfopt || "")) {
                setEditedSpace(prev => ({
                    ...prev,
                    contenttype_config: {
                        ...prev.contenttype_config,
                        postaianalysisoptions: {
                            preconfopt: prev.contenttype_config?.postaianalysisoptions?.preconfopt || "3",
                            contenttype: [systemPostConfigName],
                            postconfigs: prev.contenttype_config?.postaianalysisoptions?.postconfigs || {}
                        }
                    }
                }));
            }
        }


        setEditContentType({
            type: editedSpace.contenttype_config.contenttype.find((item: gcontenttype) => item.type == contentTypeSelectedOption)?.type || "",
            description: editedSpace.contenttype_config.contenttype.find((item: gcontenttype) => item.type == contentTypeSelectedOption)?.description || "",
            autofield: editedSpace.contenttype_config.contenttype.find((item: gcontenttype) => item.type == contentTypeSelectedOption)?.autofield ?? false
        });
        setContentTypeDetails(editedSpace.contenttype_config.contenttype.find((item: gcontenttype) => item.type == contentTypeSelectedOption));
    }, [contentTypeSelectedOption]);


    // manage selected dropdown option for content type
    useEffect(() => {
        setEditPostType({
            type: editedSpace.contenttype_config?.postaianalysisoptions?.contenttype?.find((item: gcontenttype) => item.type == postTypeSelectedOption)?.type || "",
            description: editedSpace.contenttype_config?.postaianalysisoptions?.contenttype?.find((item: gcontenttype) => item.type == postTypeSelectedOption)?.description || "",
            autofield: editedSpace.contenttype_config?.postaianalysisoptions?.contenttype?.find((item: gcontenttype) => item.type == postTypeSelectedOption)?.autofield ?? false,
        });
        setPostTypeDetails(editedSpace.contenttype_config?.postaianalysisoptions?.contenttype?.find((item: gcontenttype) => item.type == postTypeSelectedOption));

    }, [postTypeSelectedOption]);

    useEffect(() => {
        setContentTypeOptions(
            editedSpace.contenttype_config.contenttype.map((item: gcontenttype) => ({ key: item.type, text: item.type, autofield: item.autofield })) || []
        );
    }, [editedSpace.contenttype_config]);

    useEffect(() => {
        if (dpaExtTypeSansSingleTypeCustom.includes(editedSpace?.contenttype_config?.postaianalysisoptions?.preconfopt || "")) {
            const opts = editedSpace.contenttype_config?.postaianalysisoptions?.contenttype?.map((item: gcontenttype) => ({ key: item.type, text: item.type, autofield: item?.autofield })) || [...defaultContentTypeOptions];
            if (!opts.some(o => o.key === "all_content")) opts.unshift({ key: "all_content", text: "all_content", autofield: false });
            setPostTypeOptions(opts);
        }
        import.meta.env.DEV && console.log("‹post_updated›", editedSpace);
    }, [editedSpace.contenttype_config?.postaianalysisoptions]);
    useEffect(() => {
        setRandomDigits(Math.floor(Math.random() * 10000));
    }, [postTypeSelectedOption]);



    // legacy extraction config pulled from separate field
    const docextractionconfig = useMemo(() => {

        if (dpaExtTypeSansSingleTypeCustom.includes(editedSpace?.contenttype_config?.postaianalysisoptions?.preconfopt || "")) {
            try {
                setEditedSpace(prev => ({ ...prev, docextractionconfig: docextractionconfig }));
                return selectedGroup.docextractionconfig;
            } catch (e) {
                console.error("‹errr_parse›", e);
                return [];
            }


        } else {
            return selectedGroup.docextractionconfig;
        }

    }, [selectedGroup.docextractionconfig]);

    // when editing structure config - write back to main edited space state
    const writeBackStructure = (value: gextrprop[], configType: 'base' | 'post') => {
        if (editedSpace?.contenttype_config?.postaianalysisoptions?.preconfopt == "3") {
            import.meta.env.DEV && console.log("‹writes_tructure_not_3›", value);
            return;
        }
        if (configType == 'base') {
            setContentTypeDetails(prev => (prev ? { ...prev, fields: value } : prev));
            let content_type_obj: gcontenttype = {
                type: contentTypeSelectedOption,
                description: contentTypeDetails?.description || "",
                fields: value,
                autofield: contentTypeDetails?.autofield ?? false,
                qaconf: contentTypeDetails?.qaconf || []
            };
            setEditedSpace(prev => {
                const existingTypes = prev.contenttype_config.contenttype;
                const updatedTypes = existingTypes.some(ct => ct.type === content_type_obj.type)
                    ? existingTypes.map(ct => (ct.type === content_type_obj.type ? content_type_obj : ct))
                    : [...existingTypes, content_type_obj];

                return {
                    ...prev,
                    contenttype_config: {
                        ...prev.contenttype_config,
                        contenttype: updatedTypes
                    }
                };
            });
        } else if (configType == 'post') {
            setPostTypeDetails(prev => (prev ? { ...prev, fields: value } : prev));
            let content_type_obj: gcontenttype = {
                type: postTypeSelectedOption,
                description: postTypeDetails?.description || "",
                fields: value,
                autofield: postTypeDetails?.autofield ?? false,
                qaconf: postTypeDetails?.qaconf || []
            };
            setEditedSpace(prev => {
                const existingTypes = prev.contenttype_config?.postaianalysisoptions?.contenttype || [];
                const updatedTypes = existingTypes.some(ct => ct.type === content_type_obj.type)
                    ? existingTypes.map(ct => (ct.type === content_type_obj.type ? content_type_obj : ct))
                    : [...existingTypes, content_type_obj];

                return {
                    ...prev,
                    contenttype_config: {
                        ...prev.contenttype_config,
                        postaianalysisoptions: {
                            ...prev.contenttype_config.postaianalysisoptions,
                            preconfopt: prev.contenttype_config?.postaianalysisoptions?.preconfopt ?? "",
                            contenttype: updatedTypes
                        }
                    }
                };
            });
        }
    };

    // write
    const writeBackPreconfigOptions = (value: gextrprop[]) => {
        setEditedSpace(prev => ({
            ...prev,
            contenttype_config: {
                ...prev.contenttype_config,
                postaianalysisoptions: {
                    preconfopt: prev.contenttype_config?.postaianalysisoptions?.preconfopt || "3",
                    postaianalysiscustom: value,
                    contenttype: prev.contenttype_config?.postaianalysisoptions?.contenttype || [],
                    postconfigs: {
                        ...prev.contenttype_config?.postaianalysisoptions?.postconfigs,
                        [postTypeSelectedOption]: {
                            generalinstruction: prev.contenttype_config?.postaianalysisoptions?.postconfigs ? prev.contenttype_config.postaianalysisoptions.postconfigs[postTypeSelectedOption]?.generalinstruction || "" : ""
                        }
                    }
                }
            }
        }));
    };

    // manage simple edited space state
    const handleChange = (field: keyof gselectedgroup, value: string | number | boolean) => {
        setEditedSpace(prev => ({ ...prev, [field]: value }));
    };
    // manage simple edited space state
    const handlePostTypeGeneralOptions = (field: keyof geninstr, value: string) => {
        setEditedSpace(prev => {
            if (!(prev.contenttype_config?.postaianalysisoptions?.postconfigs)) {
                if (prev.contenttype_config?.postaianalysisoptions) prev.contenttype_config.postaianalysisoptions.postconfigs = {};
            }
            if (prev.contenttype_config?.postaianalysisoptions && prev.contenttype_config.postaianalysisoptions.postconfigs) {
                let prevState: geninstr = emptyGenInstr;
                if (prev.contenttype_config.postaianalysisoptions.postconfigs[postTypeSelectedOption]) {
                    prevState = prev.contenttype_config.postaianalysisoptions.postconfigs[postTypeSelectedOption];
                }
                prev.contenttype_config.postaianalysisoptions.postconfigs[postTypeSelectedOption] = { ...prevState, [field]: value };
            }

            return prev
        });
        setRandomDigits(Math.floor(Math.random() * 10000)); // force re-render to reflect changes in nested state
    };


    const normalizeGeninstr = (v: any): geninstr | null => {
        if (!v || typeof v !== "object") return null;
        return { generalinstruction: String(v.generalinstruction ?? "") };
      };

    // safe
    const handleSave = (nextPostTypeSelectedOption?: string | number) => {
        setIsLoadingAPI(true);
        let cleanedProps = cleanTrProps(
            typeof editedSpace.docextractionconfig == "string" ? JSON.parse(editedSpace.docextractionconfig || "{}") : editedSpace.docextractionconfig
        );

        setEditedSpace(prev => ({ ...prev, docextractionconfig: cleanedProps }));
        let abortSave = false;
        editedSpace.contenttype_config.contenttype.map(ct => {
            const names = ct.fields.map(f => f.name);
            const seen = new Set();
            const duplicates = [];
            for (const n of names) {
                if (seen.has(n)) duplicates.push(n);
                else seen.add(n);
            }
            if (duplicates.length > 0) {
                toast.error(`Duplicate field names in content type "${ct.type}": ${duplicates.join(", ")}. Please ensure all field names are unique.`);
                abortSave = true;
            }
        });
        if (abortSave) {
            setIsLoadingAPI(false);
            return;
        }
        editedSpace.contenttype_config?.postaianalysisoptions?.contenttype?.map(ct => {
            const names = ct.fields.map(f => f.name);
            const seen = new Set();
            const duplicates = [];
            for (const n of names) {
                if (seen.has(n)) duplicates.push(n);
                else seen.add(n);
            }
            if (duplicates.length > 0) {
                toast.error(`Duplicate field names in content type "${ct.type}": ${duplicates.join(", ")}. Please ensure all field names are unique.`);
                abortSave = true;
            }
        });

        if (abortSave) {
            setIsLoadingAPI(false);
            return;
        }
        import.meta.env.DEV && console.log("‹fin_save›", editedSpace.contenttype_config.postaianalysisoptions);
        fetch("/qDocExtrConf", {
            method: "POST",
            headers: getHeaderG(loggedInUser),
            body: JSON.stringify({
                groupid: editedSpace.selectionId,
                docextractionconfig: JSON.stringify(cleanedProps),
                docextractionuseauto: editedSpace.docextractionuseauto,
                custaiinst: editedSpace.custaiinst,
                convo_config: editedSpace.convo_config,
                contenttype_config: editedSpace.contenttype_config
            })
        }).then(() => {
            setIsLoadingAPI(false);
            onEditDocExtractionDone();
            if(nextPostTypeSelectedOption) setPostTypeSelectedOption(nextPostTypeSelectedOption.toString())
        });
    };

    // add content type
    const handleAddContentType = (configType: 'base' | 'post') => {
        if (configType == 'base') {
            if (newContentType.type && newContentType.description) {
                const newContentTypeObj: gcontenttype = {
                    type: cleanValues(newContentType.type),
                    description: newContentType.description,
                    fields: [],
                    autofield: newContentType.autofield
                };
                setEditedSpace(prev => ({
                    ...prev,
                    contenttype_config: {
                        ...prev.contenttype_config,
                        contenttype: [...prev.contenttype_config.contenttype, newContentTypeObj]
                    }
                }));

                setContentTypeDetails(newContentTypeObj);
                setContentTypeOptions(prev => [...prev, { key: newContentTypeObj.type, text: newContentTypeObj.type }]);
                toggleIsCalloutVisible();
                setContentTypeSelectedOption(newContentTypeObj.type);
                setNewContentType(new_content_type_config);
            }
        } else if (configType == 'post') {
            if (newPostType.type && newPostType.description) {
                const newPostContentTypeObj: gcontenttype = {
                    type: cleanValues(newPostType.type),
                    description: newPostType.description,
                    fields: [],
                    autofield: newPostType.autofield
                };
                setEditedSpace(prev => ({
                    ...prev,
                    contenttype_config: {
                        ...prev.contenttype_config,
                        postaianalysisoptions: {
                            ...(prev.contenttype_config?.postaianalysisoptions ?? {}),
                            preconfopt: prev.contenttype_config?.postaianalysisoptions?.preconfopt ?? "",
                            contenttype: [...(prev.contenttype_config?.postaianalysisoptions?.contenttype || []), newPostContentTypeObj]
                        }
                    }
                }));
                setPostTypeDetails(newPostContentTypeObj);
                setPostTypeOptions(prev => [...prev, { key: newPostContentTypeObj.type, text: newPostContentTypeObj.type }]);
                toggleIsPostCalloutVisible();
                setPostTypeSelectedOption(newPostContentTypeObj.type);
                setNewPostType(new_content_type_config);
            }
        }
    };

    // edit content type
    const handleEditContentType = (configType: 'base' | 'post') => {
        if (configType == 'base') {
            if (editContentType.type) {
                const updatedContentTypeObj: gcontenttype = {
                    type: cleanValues(editContentType.type),
                    description: editContentType.description,
                    fields: contentTypeDetails?.fields || [],
                    autofield: editContentType.autofield
                };

                setEditedSpace(prev => {
                    const existingTypes = prev.contenttype_config.contenttype;
                    const updatedTypes = existingTypes.some(ct => ct.type == contentTypeSelectedOption)
                        ? existingTypes.map(ct => (ct.type == contentTypeSelectedOption ? updatedContentTypeObj : ct))
                        : [...existingTypes, updatedContentTypeObj];
                    return {
                        ...prev,
                        contenttype_config: {
                            ...prev.contenttype_config,
                            contenttype: updatedTypes
                        }
                    };
                });
                setContentTypeDetails(updatedContentTypeObj);
                setContentTypeSelectedOption(editContentType.type);
                // Clear
                setEditContentType(new_content_type_config);

                toggleIsEditCalloutVisible();
            }
        } else if (configType == 'post') {
            if (editPostType.type) {
                const updatedPostContentTypeObj: gcontenttype = {
                    type: cleanValues(editPostType.type),
                    description: editPostType.description,
                    fields: postTypeDetails?.fields || [],
                    autofield: editPostType.autofield
                };
                setEditedSpace(prev => {
                    const existingTypes = prev.contenttype_config?.postaianalysisoptions?.contenttype || [];
                    const updatedTypes = existingTypes.some(ct => ct.type == postTypeSelectedOption)
                        ? existingTypes.map(ct => (ct.type == postTypeSelectedOption ? updatedPostContentTypeObj : ct))
                        : [...existingTypes, updatedPostContentTypeObj];
                    return {
                        ...prev,
                        contenttype_config: {
                            ...prev.contenttype_config,
                            postaianalysisoptions: {
                                ...(prev.contenttype_config?.postaianalysisoptions ?? {}),
                                preconfopt: prev.contenttype_config?.postaianalysisoptions?.preconfopt ?? "",
                                contenttype: updatedTypes
                            }
                        }
                    };
                });
                setPostTypeDetails(updatedPostContentTypeObj);
                setPostTypeSelectedOption(editPostType.type);
                // Clear
                setEditPostType(new_content_type_config);

                toggleIsEditPostCalloutVisible();
            }
        }
    };

    // content type delete
    const handleDelete = (confType: string) => {
        const userConfirmed = window.confirm("Are you sure you want to mark for deletion this configuration? You'll still have to save the changes.");
        if (userConfirmed) handleDeleteContentType(confType);
    };

    // manage delete cont type
    const handleDeleteContentType = (confType: string) => {
        if (confType == 'base') {
            const updatedContentTypes = editedSpace.contenttype_config.contenttype.filter(item => item.type !== contentTypeSelectedOption);
            setEditedSpace(prev => ({
                ...prev,
                contenttype_config: {
                    ...prev.contenttype_config,
                    contenttype: updatedContentTypes
                }
            }));
            setContentTypeSelectedOption(updatedContentTypes[0]?.type || systemExtrConfigName.type);
        } else if (confType == 'post') {
            const updatedPostContentTypes = editedSpace.contenttype_config?.postaianalysisoptions?.contenttype?.filter(item => item.type != postTypeSelectedOption);
            setEditedSpace(prev => ({
                ...prev,
                contenttype_config: {
                  ...prev.contenttype_config,
                  postaianalysisoptions: {
                    ...(prev.contenttype_config?.postaianalysisoptions ?? {}),
                    preconfopt: prev.contenttype_config?.postaianalysisoptions?.preconfopt ?? "",
                    contenttype: updatedPostContentTypes,
                    postconfigs: prev.contenttype_config?.postaianalysisoptions?.postconfigs ?? {}
                  },
                },
              }));
            setPostTypeSelectedOption(updatedPostContentTypes?.[0]?.type || systemPostConfigName.type);
        }
    };

    const getGeneralInstructionForSelectedPostType = (): string => {
        if (editedSpace.contenttype_config?.postaianalysisoptions?.postconfigs) {
            const config = editedSpace.contenttype_config.postaianalysisoptions.postconfigs[postTypeSelectedOption];
            return config?.generalinstruction || '';
        }
        return '';
    };
    const getWhenDoneSendToUrlForSelectedPostType = (): string => {
        if (editedSpace.contenttype_config?.postaianalysisoptions?.postconfigs) {
            const config = editedSpace.contenttype_config.postaianalysisoptions.postconfigs[postTypeSelectedOption];
            return config?.whendonesendtourl || '';
        }
        return '';
    };
    {/* dpa doc settings */}

    return (
        <div className={styles.editModalWrapDocFieldSettings}>
            <Stack styles={stackStyles} tokens={{ childrenGap: 10 }} className={styles.docFieldSettingsMainStack}>
                {/* Title */}
                <div className={styles.editFormSectionTitleV2}>
                    <div>{"Universal Content"}</div>
                </div>

                {/* toggle for allow_human_result_edits 1 - 0 */}
                <Stack horizontal tokens={{ childrenGap: 30 }} className={styles.topWrapDocExtrSettings} horizontalAlign="start">
                    <TooltipHost content={tooltipHtmlBasic("Allow Manual Edits", "Allow manual editing of post analysis extraction results.")} directionalHint={DirectionalHint.bottomLeftEdge} calloutProps={tooltipStyles}>
                        <Toggle
                            label="Allow Manual Edits"
                            inlineLabel
                            className={styles.editSpaceCheckbox}
                            styles={toggleStylesAutBaseExtraction}
                            checked={editedSpace.contenttype_config?.allow_human_result_edits == 1}
                            onChange={(_, checked) =>
                                setEditedSpace(prev => ({
                                    ...prev,
                                    contenttype_config: {
                                        ...prev.contenttype_config,
                                        allow_human_result_edits: checked ? 1 : 0
                                    }
                                }))
                            }
                        />
                    </TooltipHost>
                    {/* toggle for show_base_extract_in_preview 1 - 0 */}
                    <TooltipHost content={tooltipHtmlBasic("Analysis Preview Contents", "Show base extraction data after advanced analysis results in the analysis preview which is available directly in the item item list.")} directionalHint={DirectionalHint.bottomLeftEdge} calloutProps={tooltipStyles}>
                        <Toggle
                            label="Show Base Extract in Preview"
                            inlineLabel
                            className={styles.editSpaceCheckbox}
                            styles={toggleStylesAutBaseExtraction}
                            checked={editedSpace.contenttype_config?.show_base_extract_in_preview == 1}
                            onChange={(_, checked) =>
                                setEditedSpace(prev => ({
                                    ...prev,
                                    contenttype_config: {
                                        ...prev.contenttype_config,
                                        show_base_extract_in_preview: checked ? 1 : 0
                                    }
                                }))
                            }
                        />
                    </TooltipHost>
                </Stack>

                <Stack horizontal tokens={{ childrenGap: 10 }} className={`${styles.topWrapDocExtrSettings} ${styles.stickyExtractionHeader}`} horizontalAlign="space-between">
                    <div className={styles.advExtrTitle}>Advanced Analysis</div>
                </Stack>

                {/* post extraction ai analysis configuration */}
                <Stack tokens={{ childrenGap: 10 }} className={styles.docExtrTopsettings}>
                    <div className={styles.topWrapDocExtrSettings}>
                        <div className={styles.multyContEdits}>
                            <Dropdown
                                label="Post Extraction Analysis"
                                options={dpaAnalysisOptions}
                                disabled={false}
                                className={styles.docExtrContentTypeDropdown}
                                selectedKey={editedSpace?.contenttype_config?.postaianalysisoptions?.preconfopt || 1}
                                onChange={(_, option) =>
                                    setEditedSpace(prev => ({
                                        ...prev,
                                        contenttype_config: {
                                            ...prev.contenttype_config,
                                            postaianalysisoptions: {
                                                ...(prev.contenttype_config?.postaianalysisoptions ?? {}),
                                                preconfopt: option?.key as string
                                            }
                                        }
                                    }))
                                }
                                styles={dropdownContentTypeStyles}
                            />
                            {/* Content Types */}
                            {editedSpace?.contenttype_config?.postaianalysisoptions?.preconfopt == "4" && (
                                <Dropdown
                                    label="Content Types"
                                    options={postTypeOptions}
                                    disabled={false}
                                    className={styles.docExtrContentTypeDropdown}
                                    selectedKey={postTypeSelectedOption}
                                    onChange={(_, option) => {
                                        postTypeSelectedOption !== option?.key && handleSave(option?.key);
                                    }}
                                    styles={dropdownContentTypeStyles}
                                />
                            )}




                            {editedSpace?.contenttype_config?.postaianalysisoptions?.preconfopt == "4" && (
                                <div className={styles.docExtrContentTypeButtons}>
                                    {/* add conf button */}
                                    <TooltipHost content={<div dangerouslySetInnerHTML={{ __html: extrTypeTooltipTexts.AddContentType }} />} directionalHint={DirectionalHint.topAutoEdge} calloutProps={tooltipStyles}>
                                        <IconButton id={"postTypeRef"} onClick={toggleIsPostCalloutVisible} styles={dpaFieldConfButStyles}>
                                            <MdAddCircleOutline size={24} color="#00c6af" />
                                        </IconButton>
                                    </TooltipHost>

                                    {/* edit conf button */}
                                    <TooltipHost content={<div dangerouslySetInnerHTML={{ __html: extrTypeTooltipTexts.EditContentType }} />} directionalHint={DirectionalHint.topAutoEdge} calloutProps={tooltipStyles}>
                                        <IconButton
                                            id={"editPostTypeRef"}
                                            onClick={() => {
                                                setEditPostType({
                                                    type: postTypeDetails?.type || "",
                                                    description: postTypeDetails?.description || "",
                                                    autofield: postTypeDetails?.autofield ?? true
                                                });
                                                toggleIsEditPostCalloutVisible();
                                            }}
                                            disabled={postTypeSelectedOption == systemPostConfigName.type}
                                            styles={dpaFieldConfButStyles}
                                        >
                                            <BiMessageSquareEdit size={24} color="#2a8091" />
                                        </IconButton>
                                    </TooltipHost>

                                    {/* remove conf button */}
                                    <TooltipHost content={<div dangerouslySetInnerHTML={{ __html: extrTypeTooltipTexts.DeleteContentType }} />} directionalHint={DirectionalHint.topAutoEdge} calloutProps={tooltipStyles}>
                                        <IconButton
                                            onClick={() => {handleDelete('post')}}
                                            disabled={postTypeSelectedOption == systemPostConfigName.type}
                                            styles={dpaFieldConfButStyles}
                                        >
                                            <RiDeleteBinLine size={30} color="#c45258" />
                                        </IconButton>
                                    </TooltipHost>
                                </div>
                            )}
                        </div>

                        {editedSpace?.contenttype_config?.postaianalysisoptions?.preconfopt == "4" && (
                            <>
                                <div className={styles.postTypeGeneralInstr}>
                                    <div className={styles.postTypeGeneralInstrTitle}>General Instruction</div>
                                    <TextField
                                        value={getGeneralInstructionForSelectedPostType()}
                                        onChange={e => handlePostTypeGeneralOptions('generalinstruction',  e.currentTarget.value)}
                                        multiline
                                        placeholder="General Instructions"
                                        className={`${styles.fieldInputTT} ${styles.fieldInputFullWidth}`}
                                        styles={textFieldStylesMultilineDocExtrDPA}
                                        resizable={false}
                                        autoAdjustHeight={true}
                                    />
                                </div>
                                {/* callback url to send the result to, after post analysis is complete */}
                                <TooltipHost content={tooltipHtmlBasic("Callback URL", "URL to send results to, after post analysis is complete.")}>
                                    <div className={styles.postTypeGeneralInstr}>
                                        <div className={styles.postTypeGeneralInstrTitle}>Callback</div>
                                        <TextField
                                            value={getWhenDoneSendToUrlForSelectedPostType()}
                                            onChange={e => handlePostTypeGeneralOptions('whendonesendtourl',  e.currentTarget.value)}
                                            placeholder="When Complete, Send Results To This URL"
                                            className={`${styles.fieldInputTT} ${styles.fieldInputFullWidth}`}
                                            styles={textFieldStylesDocExtrDPA}
                                            resizable={false}
                                        />
                                    </div>
                                </TooltipHost>
                            </>
                        )}




                        {/* multiple content types post analysis configuration */}
                        {editedSpace?.contenttype_config?.postaianalysisoptions?.preconfopt == "4" && (
                            <div className={styles.jsonEditDocExtr}>
                                {/* json field input */}
                                <DocFieldSelectionV2
                                    initialProperties={postTypeDetails?.fields}
                                    onChange={updated => {writeBackStructure(updated || [], 'post')}}
                                    isRoot={true}
                                    fileList={fileList}
                                />
                            </div>
                        )}



                    </div>

                    {/* one type post analysis configuration */}
                    {editedSpace?.contenttype_config?.postaianalysisoptions?.preconfopt == "3" && (
                        <DocFieldSelectionV2
                            initialProperties={editedSpace?.contenttype_config?.postaianalysisoptions?.postaianalysiscustom || []}
                            onChange={updated => {writeBackPreconfigOptions(updated || [])}}
                            isRoot={true}
                            fileList={fileList}
                            comp_title="Content Structure"
                            comp_subtitle="Click 'Add New Field' to start defining the fields you want to get extracted"
                        />
                    )}
                </Stack>

                <Stack horizontal tokens={{ childrenGap: 10 }} className={`${styles.topWrapDocExtrSettings} ${styles.stickyExtractionHeader}`} horizontalAlign="space-between">
                    <div className={styles.baseExtrTitle}>Base Extraction</div>
                </Stack>

                {/* auto field determination for initial extraction */}
                <Stack horizontal tokens={{ childrenGap: 10 }}  className={styles.docExtrTopsettingsBase} wrap={true} grow={true} horizontalAlign="space-between">

                    <TooltipHost content={tooltxt_toggle_dpa_extr_auto} directionalHint={DirectionalHint.bottomLeftEdge} calloutProps={tooltipStyles} hostClassName={styles.mmmeuVer}>
                        <Toggle
                            label="Auto Base Extraction"
                            inlineLabel
                            className={styles.autoBaseExtractionToggle}
                            checked={editedSpace.docextractionuseauto == 1}
                            styles={toggleStylesAutBaseExtraction}
                            onChange={(_event, checked) => {
                                handleChange("docextractionuseauto", checked ? 1 : 0);
                            }}
                        />
                    </TooltipHost>
                    <TooltipHost content="Align page rotation before extraction." directionalHint={DirectionalHint.bottomLeftEdge} calloutProps={tooltipStyles} hostClassName={styles.mmmeuVer}>
                        <Toggle
                            label="Align Rotation"
                            inlineLabel
                            className={styles.editSpaceCheckbox}
                            styles={toggleStylesAutBaseExtraction}
                            disabled={false}
                            checked={editedSpace.contenttype_config?.align_rotation == 1}
                            onChange={(_, checked) =>
                                setEditedSpace(prev => ({
                                    ...prev,
                                    contenttype_config: {
                                        ...prev.contenttype_config,
                                        align_rotation: checked ? 1 : 0
                                    }
                                }))
                            }
                        />
                    </TooltipHost>
                    <TooltipHost content={tooltxt_toggle_dpa_visualai} directionalHint={DirectionalHint.bottomLeftEdge} calloutProps={tooltipStyles} hostClassName={styles.mmmeuVer}>
                        <Toggle
                            label="Force Vision AI"
                            inlineLabel
                            className={styles.editSpaceCheckbox}
                            styles={toggleStylesAutBaseExtraction}
                            disabled={false}
                            checked={editedSpace.contenttype_config?.force_visual_ai == true}
                            onChange={(_, checked) =>
                                setEditedSpace(prev => ({
                                    ...prev,
                                    contenttype_config: {
                                        ...prev.contenttype_config,
                                        force_visual_ai: editedSpace?.contenttype_config?.force_visual_ai == true ? false : true
                                    }
                                }))
                            }
                        />
                    </TooltipHost>
                    <TooltipHost content={tooltxt_toggle_dpa_highai} directionalHint={DirectionalHint.bottomLeftEdge} calloutProps={tooltipStyles} hostClassName={styles.mmmeuVer}>
                        <Toggle
                            label="High Intel Post"
                            inlineLabel
                            className={styles.editSpaceCheckbox}
                            styles={toggleStylesAutBaseExtraction}
                            disabled={false}
                            checked={editedSpace.contenttype_config?.high_intel_post == true}
                            onChange={(_, checked) =>
                                setEditedSpace(prev => ({
                                    ...prev,
                                    contenttype_config: {
                                        ...prev.contenttype_config,
                                        high_intel_post: editedSpace?.contenttype_config?.high_intel_post == true ? false : true
                                    }
                                }))
                            }
                        />
                    </TooltipHost>
                    <TooltipHost content="Provide extended context during post processing." directionalHint={DirectionalHint.bottomLeftEdge} calloutProps={tooltipStyles}>
                        <Toggle
                            label="Extended Post"
                            inlineLabel
                            className={styles.editSpaceCheckbox}
                            styles={toggleStylesAutBaseExtraction}
                            disabled={false}
                            checked={editedSpace.contenttype_config?.extended_post == 1}
                            onChange={(_, checked) =>
                                setEditedSpace(prev => ({
                                    ...prev,
                                    contenttype_config: {
                                        ...prev.contenttype_config,
                                        extended_post: checked ? 1 : 0
                                    }
                                }))
                            }
                        />
                    </TooltipHost>
                    <TooltipHost content="Limit number of pages extracted. Will slash the length of the final document. Useful if all needed information is on the first few pages." directionalHint={DirectionalHint.bottomLeftEdge} calloutProps={tooltipStyles} hostClassName={styles.languageDropdownTooltipContent}>
                        <Dropdown
                            label="Limit Pages for Extraction"
                            options={[
                                { key: 0, text: "All (no limit)" },
                                { key: 1, text: "1" },
                                { key: 2, text: "2" },
                                { key: 3, text: "3" },
                                { key: 4, text: "4" },
                                { key: 5, text: "5" },
                                { key: 6, text: "6" },
                                { key: 7, text: "7" },
                                { key: 8, text: "8" },
                                { key: 9, text: "9" },
                                { key: 10, text: "10" }
                            ]}
                            selectedKey={editedSpace.contenttype_config?.limit_pages_for_extraction ?? 0}
                            onChange={(_, option) =>
                                setEditedSpace(prev => ({
                                    ...prev,
                                    contenttype_config: {
                                        ...prev.contenttype_config,
                                        limit_pages_for_extraction: option?.key as number
                                    }
                                }))
                            }
                            className={styles.editSpaceCheckbox}
                            styles={dropdownContentTypeStyles}
                        />
                    </TooltipHost>
                    <TooltipHost content={tooltxt_dropdown_language} directionalHint={DirectionalHint.bottomLeftEdge} calloutProps={tooltipStyles} hostClassName={styles.languageDropdownTooltipContent}>
                        <Dropdown
                            label="Language"
                            options={[
                                { key: "auto", text: "Auto" },
                                { key: "bg-BG", text: "Bulgarian" },
                                { key: "en-US", text: "English" }
                            ]}
                            selectedKey={editedSpace.contenttype_config?.hardcode_language || "auto"}
                            onChange={(_, option) =>
                                setEditedSpace(prev => ({
                                    ...prev,
                                    contenttype_config: {
                                        ...prev.contenttype_config,
                                        hardcode_language: (option?.key as string) || "auto"
                                    }
                                }))
                            }
                            className={styles.editSpaceCheckbox}
                            styles={dropdownContentTypeStyles}
                        />
                    </TooltipHost>
                </Stack>

                {/* initial extraction definitions */}
                {editedSpace.docextractionuseauto === 0 && (
                    <div className={styles.docExtrContentType}>
                        <div className={styles.dropDownSection}>
                            {/* title */}
                            <div className={styles.confNameTitle}>
                                <div>{contentTypeSelectedOption}</div>
                                <div className={styles.confDescr}>{contentTypeDetails?.description}</div>
                            </div>
                            {/* toggle auto extraction next to content types */}
                            <TooltipHost content={<div dangerouslySetInnerHTML={{ __html: extrTypeTooltipTexts.ToggleAutoExtractionConf }} />} directionalHint={DirectionalHint.topAutoEdge} calloutProps={tooltipStyles}>
                                {/* toggle auto extraction next to content types */}
                                {editContentType?.autofield == true ? (
                                    <IconButton
                                        title=""
                                        onClick={() => {
                                            setContentTypeDetails(prev => (prev ? { ...prev, autofield: !prev.autofield } : prev));
                                            setEditContentType(prev => (prev ? { ...prev, autofield: false } : prev));
                                        }}
                                        styles={{
                                            root: {
                                                borderRadius: "8px",
                                                height: "32px",
                                                width: "32px",
                                                marginTop: "4px"
                                            }
                                        }}
                                    >
                                        <MdOutlineAutoFixHigh size={25} color="#ff9e9e" />
                                    </IconButton>
                                ) : (
                                    <IconButton
                                        title=""
                                        onClick={() => {
                                            setContentTypeDetails(prev => (prev ? { ...prev, autofield: !prev.autofield } : prev));
                                            setEditContentType(prev => (prev ? { ...prev, autofield: true } : prev));
                                        }}
                                        styles={{
                                            root: {
                                                borderRadius: "8px",
                                                height: "32px",
                                                width: "32px",
                                                marginTop: "4px"
                                            }
                                        }}
                                    >
                                        <MdOutlineAutoFixOff size={25} color="#b38aae" />
                                    </IconButton>
                                )}
                            </TooltipHost>
                            {/* Content Type Dropdown */}
                            <Dropdown
                                label="Content Types"
                                options={contentTypeOptions}
                                disabled={false}
                                className={styles.docExtrContentTypeDropdown}
                                selectedKey={contentTypeSelectedOption}
                                onChange={(_, option) => contentTypeSelectedOption !== option?.key && setContentTypeSelectedOption(option?.key as string)}
                                styles={dropdownContentTypeStyles}
                            />
                            <div className={styles.docExtrContentTypeButtons}>
                                {/* add conf button */}
                                <TooltipHost content={<div dangerouslySetInnerHTML={{ __html: extrTypeTooltipTexts.AddContentType }} />} directionalHint={DirectionalHint.topAutoEdge} calloutProps={tooltipStyles}>
                                    <IconButton id={"addTypeRef"} onClick={toggleIsCalloutVisible} title="" styles={dpaFieldConfButStyles} >
                                        <MdAddCircleOutline size={24} color="#00c6af" />
                                    </IconButton>
                                </TooltipHost>
                                {/* Edit Conf Button */}
                                <TooltipHost content={<div dangerouslySetInnerHTML={{ __html: extrTypeTooltipTexts.EditContentType }} />} directionalHint={DirectionalHint.topAutoEdge} calloutProps={tooltipStyles}>
                                    <IconButton
                                        id={"editTypeRef"}
                                        onClick={() => {
                                            setEditContentType({
                                                type: contentTypeDetails?.type || "",
                                                description: contentTypeDetails?.description || "",
                                                autofield: contentTypeDetails?.autofield ?? true
                                            });
                                            toggleIsEditCalloutVisible();
                                        }}
                                        title=""
                                        disabled={contentTypeSelectedOption == systemExtrConfigName.type}
                                        styles={dpaFieldConfButStyles}
                                    >
                                        <BiMessageSquareEdit size={24} color="#2a8091" />
                                    </IconButton>
                                </TooltipHost>

                                {/* remove conf button */}
                                <TooltipHost content={<div dangerouslySetInnerHTML={{ __html: extrTypeTooltipTexts.DeleteContentType }} />} directionalHint={DirectionalHint.topAutoEdge} calloutProps={tooltipStyles}>
                                    <IconButton onClick={() => {handleDelete('base')}} title="" disabled={contentTypeSelectedOption == systemExtrConfigName.type} styles={dpaFieldConfButStyles} >
                                        <RiDeleteBinLine size={30} color="#c45258" />
                                    </IconButton>
                                </TooltipHost>
                            </div>
                        </div>

                        {/* base base extraction field config - json field input */}
                        {contentTypeDetails?.autofield != true ? (
                            <div className={styles.jsonEditDocExtr}>
                                {/* base extraction field config */}
                                <DocFieldSelectionV2
                                    initialProperties={contentTypeDetails?.fields}
                                    onChange={updated => {writeBackStructure(updated || [], 'base')}}
                                    isRoot={true}
                                    fileList={fileList}
                                />
                            </div>
                        ) : (
                            <div className={styles.docExtrAutoFieldText}>
                                <div className={styles.docExtrAutoFieldTextDesc}>
                                    <div className={styles.noExtractDefinitionsContainerMain}>
                                        <div className={styles.noExtractDefinitionsText}>
                                            <MdOutlineAutoFixHigh size={25} color="#ff9e9e" />
                                            <div>Auto Extraction Enabled</div>
                                            <div>The assistant will automatically determine how to structure the data and what is important to extract.</div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>
                )}

                <div className={styles.docExtrSaveButs}>
                    <div />
                    <PrimaryButton
                        text="Save"
                        onClick={() => {handleSave()}}
                        disabled={isLoadingAPI}
                        className={styles.saveSpaceButton}
                        styles={{ root: { borderRadius: "8px" },label: { fontSize: "14px", fontFamily: "Urbanist" }}}
                    />
                </div>
            </Stack>

            {/* add new content type callout */}
            {isCalloutVisible && (
                <Callout
                    className={styles.callout}
                    styles={addUrlCallout}
                    gapSpace={0}
                    target={"#addTypeRef"}
                    onDismiss={toggleIsCalloutVisible}
                    setInitialFocus
                >
                    <Stack tokens={{ childrenGap: 10 }} padding={20}>
                        <div className={styles.addContentTypeCalloutTitle}>Add New Content Type</div>
                        <TooltipHost
                            content={"Descriptive Name of the Content Type"}
                            directionalHint={DirectionalHint.topAutoEdge}
                            calloutProps={tooltipStyles}
                        >
                            <TextField
                                label="Content Type Name"
                                styles={textFieldModalContentTypeCreateStyles}
                                value={newContentType.type}
                                onChange={(_, newValue) => setNewContentType(prev => ({ ...prev, type: cleanValues(newValue || "") }))}
                            />
                        </TooltipHost>
                        <TooltipHost
                            content={"Instruct the assistant on how to recognize this content type when it ingests content"}
                            directionalHint={DirectionalHint.topAutoEdge}
                            calloutProps={tooltipStyles}
                        >
                            <TextField
                                label="Content Type AI Instructions"
                                styles={textFieldModalContentTypeCreateStyles}
                                multiline
                                value={newContentType.description}
                                onChange={(_, newValue) => setNewContentType(prev => ({ ...prev, description: newValue || "" }))}
                            />
                        </TooltipHost>
                        <PrimaryButton
                            text="Add"
                            onClick={() => {
                                handleAddContentType('base');
                            }}
                            styles={{
                                root: { width: "100%", borderRadius: "8px", marginTop: "10px" },
                                label: { fontSize: "14px", fontFamily: "Urbanist" }
                            }}
                        />
                    </Stack>
                </Callout>
            )}

            {isPostCalloutVisible && (
                <Callout
                    className={styles.callout}
                    styles={addUrlCallout}
                    gapSpace={0}
                    target={"#postTypeRef"}
                    onDismiss={toggleIsPostCalloutVisible}
                    setInitialFocus
                >
                    <Stack tokens={{ childrenGap: 10 }} padding={20}>
                        <div className={styles.addContentTypeCalloutTitle}>Add New Content Type</div>
                        <TooltipHost
                            content={"Descriptive Name of the Content Type"}
                            directionalHint={DirectionalHint.topAutoEdge}
                            calloutProps={tooltipStyles}
                        >
                            <TextField
                                label="Content Type Name"
                                styles={textFieldModalContentTypeCreateStyles}
                                value={newPostType.type}
                                onChange={(_, newValue) => setNewPostType(prev => ({ ...prev, type: cleanValues(newValue || "") }))}
                            />
                        </TooltipHost>
                        <TooltipHost
                            content={"Instruct the assistant on how to recognize this content type when it ingests content"}
                            directionalHint={DirectionalHint.topAutoEdge}
                            calloutProps={tooltipStyles}
                        >
                            <TextField
                                label="Content Type AI Instructions"
                                styles={textFieldModalContentTypeCreateStyles}
                                multiline
                                value={newPostType.description}
                                onChange={(_, newValue) => setNewPostType(prev => ({ ...prev, description: newValue || "" }))}
                            />
                        </TooltipHost>
                        <PrimaryButton
                            text="Add"
                            onClick={() => {
                                handleAddContentType('post');
                            }}
                            styles={{
                                root: { width: "100%", borderRadius: "8px", marginTop: "10px" },
                                label: { fontSize: "14px", fontFamily: "Urbanist" }
                            }}
                        />
                    </Stack>
                </Callout>
            )}

            {/* edit content type callout */}
            {isEditCalloutVisible && (
                <Callout
                    className={styles.callout}
                    styles={addUrlCallout}
                    gapSpace={0}
                    target={"#editTypeRef"}
                    onDismiss={toggleIsEditCalloutVisible}
                    setInitialFocus
                >
                    <Stack tokens={{ childrenGap: 10 }} padding={20}>
                        <div className={styles.addContentTypeCalloutTitle}>Edit Content Type</div>
                        <TooltipHost
                            content={"Descriptive Name of the Content Type"}
                            directionalHint={DirectionalHint.topAutoEdge}
                            calloutProps={tooltipStyles}
                        >
                            <TextField
                                label="Content Type Name"
                                styles={textFieldModalContentTypeCreateStyles}
                                value={editContentType.type}
                                onChange={(_, newValue) => setEditContentType(prev => ({ ...prev, type: cleanValues(newValue || "") }))}
                            />
                        </TooltipHost>
                        <TooltipHost content={extrConfConfLabels.mutliTextLabel} directionalHint={DirectionalHint.rightBottomEdge} calloutProps={tooltipStyles}>
                            <TextField
                                label="Content Type AI Instructions"
                                styles={textFieldModalContentTypeCreateStyles}
                                multiline
                                value={editContentType.description}
                                onChange={(_, newValue) => setEditContentType(prev => ({ ...prev, description: newValue || "" }))}
                            />
                        </TooltipHost>
                        <PrimaryButton
                            text="Save"
                            onClick={() => {
                                handleEditContentType('base');
                            }}
                            styles={{
                                root: { width: "100%", borderRadius: "8px", marginTop: "10px" },
                                label: { fontSize: "14px", fontFamily: "Urbanist" }
                            }}
                        />
                    </Stack>
                </Callout>
            )}

            {isEditPostCalloutVisible && (
                <Callout
                    className={styles.callout}
                    styles={addUrlCallout}
                    gapSpace={0}
                    target={"#editPostTypeRef"}
                    onDismiss={toggleIsEditPostCalloutVisible}
                    setInitialFocus
                >
                    <Stack tokens={{ childrenGap: 10 }} padding={20}>
                        <div className={styles.addContentTypeCalloutTitle}>Edit Content Type</div>
                        <TooltipHost
                            content={"Descriptive Name of the Content Type"}
                            directionalHint={DirectionalHint.topAutoEdge}
                            calloutProps={tooltipStyles}
                        >
                            <TextField
                                label="Content Type Name"
                                styles={textFieldModalContentTypeCreateStyles}
                                value={editPostType.type}
                                onChange={(_, newValue) => setEditPostType(prev => ({ ...prev, type: cleanValues(newValue || "") }))}
                            />
                        </TooltipHost>
                        <TooltipHost content={extrConfConfLabels.mutliTextLabel} directionalHint={DirectionalHint.rightBottomEdge} calloutProps={tooltipStyles}>
                            <TextField
                                label="Content Type AI Instructions"
                                styles={textFieldModalContentTypeCreateStyles}
                                multiline
                                value={editPostType.description}
                                onChange={(_, newValue) => setEditPostType(prev => ({ ...prev, description: newValue || "" }))}
                            />
                        </TooltipHost>
                        <PrimaryButton
                            text="Save"
                            onClick={() => {
                                handleEditContentType('post');
                            }}
                            styles={{
                                root: { width: "100%", borderRadius: "8px", marginTop: "10px" },
                                label: { fontSize: "14px", fontFamily: "Urbanist" }
                            }}
                        />
                    </Stack>
                </Callout>
            )}
        </div>
    );
};
